import { RouterView, type RouteRecordRaw } from "vue-router";
import { computed } from "vue";
import { NavigationTab } from "@/components/Navigation";
import { useHasManagerCapability } from "@/services/user";
import { visibleSettingsEntries } from "@/router/settings_menu";

export const publicDashboardRoute: RouteRecordRaw[] = [
  {
    path: "shared-dashboard/:pk",
    name: "shared-dashboard",
    meta: {
      activityArea: null,
      publicShare: "always",
      title: "Shared Dashboard",
      requiresAuth: false,
      shell: "reader",
      navbar: "hidden",
      sidepanel: "hidden",
      contentWidth: "full-bleed",
      breadcrumb: false,
    },
    component: () => import("@/views/Dashboard/SharedDashboard.vue"),
  },
];

// Nested under the Channels layout with an absolute path so the URL stays
// /settings/add-account/... — OAuth redirect URLs in the deployment repo depend
// on it. No component of its own: children render in the layout's RouterView.
export const addResourceRoute: RouteRecordRaw = {
  path: "/settings/add-account",
  name: "add-resource",
  redirect: { name: "select-channel" },
  meta: {
    activityArea: "data_sources",
    title: "Add Account",
    text: "Add new account",
    description: "Connect new accounts to your organization",
    icon: "plus",
    breadcrumb: { label: "Channels", to: { name: "resources" } },
  },
  children: [
    {
      path: "select-channel",
      name: "select-channel",
      meta: {
        title: "Select Channel",
        breadcrumb: { label: "Select channel" },
      },
      component: () =>
        import("@/views/OrganizationSettings/AddResourceMenuSteps/SelectChannelStep.vue"),
    },
    {
      path: "xandr-auth",
      name: "xandr-auth",
      meta: { title: "Xandr Auth", breadcrumb: { label: "Xandr" } },
      component: () =>
        import("@/views/OrganizationSettings/AddResourceMenuSteps/XandrAuthStep.vue"),
    },
    {
      path: "kobler-auth",
      name: "kobler-auth",
      meta: { title: "Kobler Auth", breadcrumb: { label: "Kobler" } },
      component: () =>
        import("@/views/OrganizationSettings/AddResourceMenuSteps/KoblerAuthStep.vue"),
    },
    {
      path: "c-alt-auth",
      name: "c-alt-auth",
      meta: { title: "C·ALT Auth", breadcrumb: { label: "C·ALT" } },
      component: () =>
        import("@/views/OrganizationSettings/AddResourceMenuSteps/CAltAuthStep.vue"),
    },
    {
      path: "matomo-auth",
      name: "matomo-auth",
      meta: { title: "Matomo Auth", breadcrumb: { label: "Matomo" } },
      component: () =>
        import("@/views/OrganizationSettings/AddResourceMenuSteps/MatomoAuthStep.vue"),
    },
    {
      path: "omnium-auth",
      name: "omnium-auth",
      meta: { title: "Omnium Auth", breadcrumb: { label: "Omnium" } },
      component: () =>
        import("@/views/OrganizationSettings/AddResourceMenuSteps/OmniumAuthStep.vue"),
    },
  ],
};

export const organizationRoutes: RouteRecordRaw[] = [
  // !!! IMPORTANT !!!
  // Remember to change redirect URLs in the deployment repo env if changing these URLs
  {
    path: "general",
    name: "general",
    meta: { title: "Organization Settings", breadcrumb: false },
    component: () =>
      import("@/views/OrganizationSettings/OrganizationSettings.vue"),
  },
  {
    path: "tags",
    name: "tags",
    meta: { activityArea: "tags", title: "Tag Management", breadcrumb: false },
    component: () => import("@/views/OrganizationSettings/TagManagement.vue"),
  },
  {
    path: "dashboards",
    redirect: { name: "dashboard-overview" },
    children: [
      {
        path: "overview",
        redirect: { name: "dashboard-overview" },
      },
      {
        path: "new",
        redirect: { name: "dashboard-overview" },
      },
      {
        path: ":pk",
        redirect: { name: "edit-dashboard" },
      },
    ],
  },
  {
    // Overview and Connect share this layout so the ActionBar and tab bar
    // stay mounted while switching tabs.
    path: "channels",
    component: () => import("@/views/OrganizationSettings/ChannelsLayout.vue"),
    children: [
      {
        path: "",
        name: "resources",
        meta: {
          activityArea: "data_sources",
          title: "Channels",
          breadcrumb: false,
        },
        component: () => import("@/views/OrganizationSettings/Resources.vue"),
      },
      addResourceRoute,
    ],
  },
  {
    path: "edit-conversions",
    name: "edit-conversions",
    meta: {
      activityArea: "data_sources",
      title: "Edit conversions",
      breadcrumb: false,
    },
    component: () =>
      import("@/views/OrganizationSettings/Conversions/EditConversions.vue"),
  },
  {
    path: "calculated-metrics",
    name: "calculated-metrics",
    meta: {
      activityArea: "data_sources",
      title: "Calculated Metrics",
      breadcrumb: false,
    },
    component: () =>
      import("@/views/OrganizationSettings/CalculatedMetrics/CalculatedMetrics.vue"),
  },
  {
    path: "automations",
    name: "organization_settings_automations",
    meta: {
      activityArea: "automations",
      title: "Automations",
      breadcrumb: false,
    },
    component: () =>
      import("@/views/OrganizationSettings/Automations/Automations.vue"),
  },
  {
    path: "changelog",
    name: "changelog",
    meta: { title: "Changelog", breadcrumb: false },
    component: () => import("@/views/OrganizationSettings/Changelog.vue"),
  },
  {
    path: "consent",
    name: "consent_settings",
    meta: { title: "Consent Settings", breadcrumb: false },
    component: () => import("@/views/OrganizationSettings/ConsentSettings.vue"),
  },
  {
    path: "mcp-applications",
    name: "mcp_applications_shell",
    redirect: { name: "mcp-applications" },
    meta: {
      title: "MCP Applications",
      breadcrumb: {
        label: "MCP Applications",
        to: { name: "mcp-applications" },
      },
    },
    component: RouterView,
    children: [
      {
        path: "",
        name: "mcp-applications",
        // The gate lives on this child, not the shell. `route.meta` is the
        // MERGED matched chain, so a `requiredFeatureFlag` on the shell is
        // inherited by every child — including the deliberately-open
        // integration guide, which `watchRouteForFeatureFlag` then 404s
        // because `paywall: false` is falsy and doesn't skip the check.
        meta: {
          title: "MCP Applications",
          breadcrumb: false,
          // Paywalled rather than hidden: the tab stays in settings and the
          // page renders the pitch, so a manager can see what MCP would give
          // them.
          //
          // "in-component" rather than `true`: PageFrame would otherwise swap
          // the whole slot including OrganizationOverview, which hosts the
          // settings tab bar below `lg` — a locked org on mobile lost every
          // way off this page. MCPApplications.vue gates itself instead.
          requiredFeatureFlag: "mcpAccess",
          paywall: "in-component",
        },
        component: () =>
          import("@/views/OrganizationSettings/MCPApplications/MCPApplications.vue"),
      },
      // Stays nested for its breadcrumb ancestry (pinned by
      // routeShellContract.spec.ts). The integration guide reads as marketing
      // and costs nothing to leave open — the MCP paywall page links to it —
      // so it inherits no feature requirement now that the gate sits on its
      // sibling. `paywall: false` additionally stops PageFrame from rendering
      // an ancestor's pitch in its place.
      {
        path: "/settings/mcp-documentation",
        name: "mcp-documentation",
        meta: {
          activityArea: null,
          title: "MCP Integration Guide",
          paywall: false,
          breadcrumb: { label: "MCP Integration Guide" },
        },
        component: () =>
          import("@/views/OrganizationSettings/MCPApplications/MCPDocumentation.vue"),
      },
    ],
  },
  {
    path: "mcp-oauth-callback",
    name: "mcp-oauth-callback",
    meta: {
      activityArea: null,
      title: "OAuth Callback",
      shell: "none",
      navbar: "hidden",
      sidepanel: "hidden",
      breadcrumb: false,
    },
    component: () =>
      import("@/views/OrganizationSettings/MCPApplications/OAuthCallback.vue"),
  },
];

export function useSettingsTabs() {
  // The backend capability, so a superuser sees the same entries the API
  // would serve them rather than the member subset.
  const isManager = useHasManagerCapability();

  // Membership and gating come from SETTINGS_MENU; this surface only picks the
  // short tab-bar labels.
  return computed((): NavigationTab[] =>
    visibleSettingsEntries(isManager.value).map((entry) => ({
      name: entry.tabName,
      href: entry.href,
    })),
  );
}
